import React, { useState } from 'react';
import { useQuery, useMutation } from '@tanstack/react-query';
import axios from 'axios';
import { API_BASE_URL } from '../config';

// Public API endpoints
const PUBLIC_API = `${API_BASE_URL}/public`;

interface CFPFormData {
  title: string;
  abstract: string;
  speaker_name: string;
  speaker_bio: string;
  speaker_position: string;
  email: string;
  organization: string;
  presentation_type: 'technology_application' | 'technology_spotlight' | 'research_output';
}

interface SubmitResponse {
  success: boolean;
  message: string;
}

const CFPForm: React.FC = () => {
  const [formData, setFormData] = useState<CFPFormData>({
    title: '',
    abstract: '',
    speaker_name: '',
    speaker_bio: '',
    speaker_position: '',
    email: '',
    organization: '',
    presentation_type: 'technology_application'
  });

  const [submitSuccess, setSubmitSuccess] = useState(false);
  const [submitError, setSubmitError] = useState<string | null>(null);

  // Query conference settings to check if CFP is open
  const { data: settings, isLoading: settingsLoading } = useQuery({
    queryKey: ['conferenceSettings'],
    queryFn: async () => {
      const response = await axios.get(`${PUBLIC_API}/conference-settings`);
      return response.data;
    }
  });

  // Mutation for submitting the form
  const submitMutation = useMutation({
    mutationFn: async (data: CFPFormData) => {
      const response = await axios.post(`${API_BASE_URL}/cfp/submit`, data);
      return response.data;
    },
    onSuccess: (data) => {
      setSubmitSuccess(true);
      setSubmitError(null);
    },
    onError: (error: any) => {
      setSubmitSuccess(false);
      // Extract validation error details from FastAPI response
      if (error.response?.data?.detail) {
        if (Array.isArray(error.response.data.detail)) {
          setSubmitError(error.response.data.detail.map((err: any) => err.msg).join('\n'));
        } else {
          setSubmitError(error.response.data.detail);
        }
      } else {
        setSubmitError(error.message);
      }
    }
  });

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    submitMutation.mutate(formData);
  };

  if (settingsLoading) {
    return <div>Loading...</div>;
  }

  if (!settings?.accepting_cfp) {
    return (
      <div className="text-center p-8">
        <h2 className="text-2xl font-bold text-gray-800">Call for Speakers is Closed</h2>
        <p className="mt-4 text-gray-600">
          The Call for Speakers period has ended. Please check back later for future opportunities.
        </p>
      </div>
    );
  }

  if (submitSuccess) {
    return (
      <div className="text-center p-8">
        <h2 className="text-2xl font-bold text-green-600">Submission Successful!</h2>
        <p className="mt-4 text-gray-600">
          Thank you for your submission. We will review your proposal and get back to you soon.
          Please check your email for further instructions on accessing your speaker dashboard.
        </p>
      </div>
    );
  }

  return (
    <div className="max-w-2xl mx-auto p-6">
      <div className="mb-6 bg-blue-50 border border-blue-200 p-4 rounded-md">
        <h3 className="text-lg font-semibold text-blue-800 mb-2">Speaker Guidelines</h3>
        <p className="text-gray-700 mb-3">
          Please review our speaker guidelines before submitting your proposal. The guidelines contain important information about presentation formats, time allocations, and expectations.
        </p>
        <div className="flex items-center">
          <a 
            href="/LI2025SpeakersGuidelines.pdf" 
            target="_blank" 
            rel="noopener noreferrer"
            className="inline-flex items-center text-blue-600 hover:text-blue-800"
          >
            <svg xmlns="http://www.w3.org/2000/svg" className="h-5 w-5 mr-1" fill="none" viewBox="0 0 24 24" stroke="currentColor">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M7 21h10a2 2 0 002-2V9.414a1 1 0 00-.293-.707l-5.414-5.414A1 1 0 0012.586 3H7a2 2 0 00-2 2v14a2 2 0 002 2z" />
            </svg>
            Download Speaker Guidelines (PDF)
          </a>
        </div>
      </div>

      <div className="mb-6 bg-yellow-50 border border-yellow-200 p-4 rounded-md">
        <h3 className="text-lg font-semibold text-yellow-800 mb-2">Important Note</h3>
        <p className="text-gray-700">
          This form is for individual presentations only (20 minutes). For panel presentations or themed workshops, 
          please contact us directly at <a href="mailto:summit@lt-innovate.org" className="text-blue-600 hover:text-blue-800">summit@lt-innovate.org</a>.
        </p>
      </div>

      <form onSubmit={handleSubmit} className="space-y-6">
        <h2 className="text-2xl font-bold text-gray-800">Submit Your Proposal</h2>
        
        {submitError && (
          <div className="bg-red-50 border border-red-200 text-red-700 px-4 py-3 rounded">
            {submitError}
          </div>
        )}

        <div>
          <label className="block text-sm font-medium text-gray-700">Presentation Title</label>
          <input
            type="text"
            required
            minLength={5}
            maxLength={200}
            value={formData.title}
            onChange={e => setFormData(prev => ({ ...prev, title: e.target.value }))}
            className="mt-1 block w-full border border-gray-300 rounded-md shadow-sm p-2"
          />
          <p className="mt-1 text-sm text-gray-500">Must be between 5 and 200 characters</p>
        </div>

        <div>
          <label className="block text-sm font-medium text-gray-700">Abstract</label>
          <textarea
            required
            minLength={100}
            maxLength={5000}
            value={formData.abstract}
            onChange={e => setFormData(prev => ({ ...prev, abstract: e.target.value }))}
            rows={4}
            className="mt-1 block w-full border border-gray-300 rounded-md shadow-sm p-2"
          />
          <p className="mt-1 text-sm text-gray-500">Must be between 100 and 5000 characters</p>
        </div>

        <div>
          <label className="block text-sm font-medium text-gray-700">Speaker Name</label>
          <input
            type="text"
            required
            minLength={2}
            maxLength={100}
            value={formData.speaker_name}
            onChange={e => setFormData(prev => ({ ...prev, speaker_name: e.target.value }))}
            className="mt-1 block w-full border border-gray-300 rounded-md shadow-sm p-2"
          />
          <p className="mt-1 text-sm text-gray-500">Must be between 2 and 100 characters</p>
        </div>

        <div>
          <label className="block text-sm font-medium text-gray-700">Current Position/Function</label>
          <input
            type="text"
            required
            minLength={2}
            maxLength={100}
            value={formData.speaker_position}
            onChange={e => setFormData(prev => ({ ...prev, speaker_position: e.target.value }))}
            className="mt-1 block w-full border border-gray-300 rounded-md shadow-sm p-2"
          />
          <p className="mt-1 text-sm text-gray-500">Your current role/position in your organization</p>
        </div>

        <div>
          <label className="block text-sm font-medium text-gray-700">Speaker Bio</label>
          <textarea
            required
            minLength={50}
            maxLength={1000}
            value={formData.speaker_bio}
            onChange={e => setFormData(prev => ({ ...prev, speaker_bio: e.target.value }))}
            rows={3}
            className="mt-1 block w-full border border-gray-300 rounded-md shadow-sm p-2"
          />
          <p className="mt-1 text-sm text-gray-500">Must be between 50 and 1000 characters</p>
        </div>

        <div>
          <label className="block text-sm font-medium text-gray-700">Email</label>
          <input
            type="email"
            required
            value={formData.email}
            onChange={e => setFormData(prev => ({ ...prev, email: e.target.value }))}
            className="mt-1 block w-full border border-gray-300 rounded-md shadow-sm p-2"
          />
        </div>

        <div>
          <label className="block text-sm font-medium text-gray-700">Organization</label>
          <input
            type="text"
            required
            value={formData.organization}
            onChange={e => setFormData(prev => ({ ...prev, organization: e.target.value }))}
            className="mt-1 block w-full border border-gray-300 rounded-md shadow-sm p-2"
          />
        </div>

        <div>
          <label className="block text-sm font-medium text-gray-700">Presentation Type</label>
          <select
            required
            value={formData.presentation_type}
            onChange={e => setFormData(prev => ({ ...prev, presentation_type: e.target.value as 'technology_application' | 'technology_spotlight' | 'research_output' }))}
            className="mt-1 block w-full border border-gray-300 rounded-md shadow-sm p-2"
          >
            <option value="technology_application">Technology application use case presentation (20 minutes)</option>
            <option value="technology_spotlight">Technology spotlight presentation (20 minutes)</option>
            <option value="research_output">Research output presentation (20 minutes)</option>
          </select>
        </div>

        <div className="flex justify-end">
          <button
            type="submit"
            disabled={submitMutation.isPending}
            className="bg-blue-600 text-white px-4 py-2 rounded-md hover:bg-blue-700 disabled:opacity-50"
          >
            {submitMutation.isPending ? 'Submitting...' : 'Submit Proposal'}
          </button>
        </div>
      </form>
    </div>
  );
};

export default CFPForm;
